import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

// Radix Dialog under the hood: focus trap, focus return, ESC, and scrim dismiss are Radix's, not hand-rolled.
export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Mono-caps eyebrow above the body. e.g. "Adjust this habit". */
  eyebrow?: ReactNode;
  /** Bold title rendered as a serif block above the body slot. */
  title?: ReactNode;
  /** Main content (lists, action buttons, prose). */
  children?: ReactNode;
  /** Optional footer row (cancel / confirm). */
  footer?: ReactNode;
  /** Sheet label for screen-reader users; defaults to the eyebrow text. */
  ariaLabel?: string;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  ariaLabel,
  className,
}: SheetProps) {
  const accessibleName =
    ariaLabel ??
    (typeof title === "string" ? title : undefined) ??
    (typeof eyebrow === "string" ? eyebrow : undefined) ??
    "Sheet";

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[color-mix(in_srgb,var(--color-navy)_32%,transparent)]" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-32px)] max-w-[360px] -translate-x-1/2 -translate-y-1/2 flex-col gap-3.5 rounded-xl bg-cream-warm p-5.5 shadow-sheet focus:outline-none",
            className,
          )}
        >
          <Dialog.Title className="sr-only">{accessibleName}</Dialog.Title>
          {(eyebrow || title) && (
            <header className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-1.5">
                {eyebrow ? (
                  <div className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                    {eyebrow}
                  </div>
                ) : null}
                {title ? (
                  <div className="rounded-md bg-cream-deep px-3.5 py-3 font-title text-lg leading-snug text-navy">
                    {title}
                  </div>
                ) : null}
              </div>
              <Dialog.Close
                aria-label="Close"
                className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-gray-400 transition-colors hover:bg-mist hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
              >
                <span aria-hidden="true" className="text-[20px] leading-none">
                  ×
                </span>
              </Dialog.Close>
            </header>
          )}
          {children ? <div className="flex flex-col gap-2">{children}</div> : null}
          {footer ? (
            <footer className="mt-1 flex items-center justify-end gap-2">{footer}</footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * SheetAction. A row inside Sheet's body slot for stacked action
 * choices. Label + note + optional disabled state. Used by the
 * "Adjust this item" sheet.
 */
export interface SheetActionProps {
  label: ReactNode;
  note?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export function SheetAction({ label, note, disabled, onClick }: SheetActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col gap-1 rounded-lg border border-mist-dark bg-transparent px-3.5 py-3 text-left transition-colors duration-200 ease-[var(--ease-out-expo)]",
        disabled ? "cursor-not-allowed opacity-50" : "hover:border-teal-300 hover:bg-teal-50",
      )}
    >
      <span className="text-[14px] font-semibold text-navy">{label}</span>
      {note ? <span className="text-sm leading-relaxed text-gray-500">{note}</span> : null}
    </button>
  );
}
